import { useMemo } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Skeleton } from "primereact/skeleton";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import {
  useDashboardSnapshot,
  useLeaderboardSnapshot,
  useEnrollments,
  useUserTournaments,
  useUserChores,
  useUserAssignments,
  useAchievements,
} from "sj-ui-toolkit/src/api/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";

import { HighlightedContent } from "./components/HighlightedContent/HighlightedContent";
import { UserSnapshot } from "./components/UserSnapshot/UserSnapshot";
import { TodoCarousel } from "./components/TodoCarousel/TodoCarousel";
import { SavedLessons } from "./components/SavedLessons/SavedLessons";
import { MyPaths } from "./components/MyPaths/MyPaths";
import { AssessmentSection } from "./components/Assessments/AssessmentSection";
import { BucketOfLearnins } from "./components/BucketOfLearnin/BucketOfLearnin";
import { formatAssessmentData } from "../../helpers/assessments";

const Dashboard = () => {
  useBreadcrumb({ label: "Dashboard", route: "/" });
  const { user } = useUserContext();
  const { enableAtlasAssessmentsUi } = useFlags();

  // Fetch data using cached API hooks
  const { data: userSnapshot, isLoading: snapshotLoading } = useDashboardSnapshot();
  const { data: leaderboardData, isLoading: leaderboardLoading } = useLeaderboardSnapshot();
  const { data: achievementsData, isLoading: achievementsLoading } = useAchievements();
  const { data: choreList, isLoading: choresLoading } = useUserChores();
  const { data: tournaments = [], isLoading: tournamentsLoading } = useUserTournaments();
  const { data: assignments = [], isLoading: assignmentsLoading } = useUserAssignments();
  const { data: currentPaths = [], isLoading: pathsLoading } = useEnrollments();

  const { settings, isFeatureEnabled } = useSettingsContext();
  const { leaderboard: showLeaderboard, strictAssignment } = settings || {};
  const userInitiatedExams = enableAtlasAssessmentsUi && isFeatureEnabled("self_assessments");

  // Derive assessment chores from full chore list
  const assessmentChores = useMemo(() => {
    return (
      choreList?.items?.filter(
        item =>
          "knowledgeAssessment" in item.content &&
          item.content.knowledgeAssessment?.status.progress !== "Completed"
      ) ?? []
    );
  }, [choreList]);

  // Should we show the assessments section?
  const showAssessmentsSection =
    userInitiatedExams && !choresLoading && assessmentChores.length === 0;

  // Derive values from cached data
  const isNewUser = userSnapshot?.lessons?.completed === 0;

  const leaderboardSnapshot = useMemo(() => {
    return leaderboardData?.users?.sort((a, b) => a.position - b.position) || [];
  }, [leaderboardData]);

  const todos = useMemo(() => {
    const assessments = assessmentChores.length ? formatAssessmentData(assessmentChores) || [] : [];

    return {
      assessments,
      tournaments: tournaments || [],
      assignments: assignments || [],
    };
  }, [assessmentChores, tournaments, assignments]);

  const hasOutstandingStrictAssignment = useMemo(() => {
    if (!strictAssignment || !assignments?.length) return false;

    return assignments.some(assignment => assignment.status !== "passed");
  }, [assignments, strictAssignment]);

  // Combined loading state for todos section
  const todosLoading = choresLoading || tournamentsLoading || assignmentsLoading;

  const welcomeText = useMemo(() => {
    // wait until snapshot loads and user is set to avoid flicker
    if (snapshotLoading || !user || !userSnapshot) return null;

    const givenName = user?.name?.givenName;
    if (givenName === "Anonymous" || givenName === undefined) {
      return isNewUser ? "Welcome to Security Journey!" : "Welcome back!";
    }

    const baseText = isNewUser ? "Welcome" : "Welcome back";
    return givenName ? `${baseText}, ${givenName}!` : `${baseText}!`;
  }, [isNewUser, user?.name?.givenName, snapshotLoading, userSnapshot]);

  const mappedEnrollments = useMemo(() => {
    const assignmentRoleIds = new Set(
      assignments?.filter(a => a.status !== "passed").map(a => a.roleId)
    );
    return currentPaths?.map(path => ({
      ...path,
      id: path.roleId,
      disabled: hasOutstandingStrictAssignment && !assignmentRoleIds.has(path.roleId),
    }));
  }, [currentPaths, assignments, hasOutstandingStrictAssignment]);

  const mappedPaths = useMemo(() => {
    return (
      mappedEnrollments?.filter(
        e => e.enrollmentType === "path" && !["brown", "black"].includes(e.levelRank)
      ) || []
    );
  }, [mappedEnrollments]);

  const mappedQuests = useMemo(() => {
    return mappedEnrollments?.filter(e => e.enrollmentType === "quest") || [];
  }, [mappedEnrollments]);

  return (
    <div data-test-id="learn-dashboard-page">
      {!welcomeText ? (
        <Skeleton width="250px" height="2.5rem" className="mb-4" />
      ) : (
        <h1 className="atlas-h2">{welcomeText}</h1>
      )}

      <HighlightedContent />
      <UserSnapshot
        userSnapshot={userSnapshot || {}}
        leaderboard={leaderboardSnapshot}
        achievements={achievementsData?.passedBelts || []}
        isLoading={snapshotLoading || leaderboardLoading || achievementsLoading}
        showLeaderboard={showLeaderboard}
      />
      <TodoCarousel
        assessments={todos.assessments}
        tournaments={todos.tournaments}
        assignments={todos.assignments}
        isLoading={todosLoading}
      />
      {showAssessmentsSection && <AssessmentSection />}
      <MyPaths currentPaths={mappedPaths} currentQuests={mappedQuests} isLoading={pathsLoading} />
      <BucketOfLearnins
        currentPaths={mappedPaths}
        currentQuests={mappedQuests}
        hasOutstandingStrictAssignment={hasOutstandingStrictAssignment}
      />

      <SavedLessons savedLessons={[]} />
    </div>
  );
};

export default Dashboard;
